import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserData, getEvaluatorsByUserId, finalizeEvaluators } from '../services/database';
import { getPlans } from '../services/plansService';
import Header from '../components/Header';
import EvaluatorList from '../components/evaluators/EvaluatorList';
import EvaluatorForm from '../components/evaluators/EvaluatorForm';
import FinalizeNominationsButton from '../components/common/FinalizeNominationsButton';
import { ArrowLeft, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plan } from '@/types/plan';
import { Evaluator } from '@/types/evaluator';
import { toast } from 'react-hot-toast';
import { Navigate } from 'react-router-dom';

export default function NominateEvaluators() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [userData, setUserData] = useState<{ firstName: string; lastName: string; onboardingCompleted?: boolean }>({ 
    firstName: '', 
    lastName: '' 
  });
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentUser]);

  // Check if onboarding is completed
  if (!loading && userData && userData.onboardingCompleted === false) {
    toast.error('Please complete the onboarding tutorial first');
    return <Navigate to="/dashboard" />;
  }

  const loadData = async () => {
    if (!currentUser) return;

    try {
      const [userDataResult, evaluatorsResult, plansResult] = await Promise.all([
        getUserData(currentUser.uid),
        getEvaluatorsByUserId(currentUser.uid),
        getPlans()
      ]);

      setUserData({
        firstName: userDataResult.firstName,
        lastName: userDataResult.lastName,
        onboardingCompleted: userDataResult.onboardingCompleted
      });

      setEvaluators(evaluatorsResult);
      const plan = plansResult.find(p => p.id === userDataResult.selectedPlanId);
      if (plan) setSelectedPlan(plan);
    } catch (error: any) {
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Redirect to sign in if not authenticated
  if (!currentUser && !loading) {
    return <Navigate to="/signin" />;
  }

  const handleFinalize = async () => {
    if (!currentUser) return;

    try {
      setSaving(true);
      await finalizeEvaluators(currentUser.uid);
      toast.success('Nominations finalized successfully');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error finalizing nominations:', error);
      toast.error('Failed to finalize nominations');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEvaluator = async (data: { firstName: string; lastName: string; email: string }) => {
    if (!currentUser) return;

    try {
      setShowForm(false);
      await loadData();
    } catch (error: any) {
      console.error('Error adding evaluator:', error);
      toast.error(error.message || 'Failed to add evaluator');
    }
  };

  const handleDeleteEvaluator = async (evaluatorId: string) => {
    try {
      await loadData();
    } catch (error: any) {
      console.error('Error deleting evaluator:', error);
      toast.error(error.message || 'Failed to delete evaluator');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      setError('Failed to log out');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const completedEvaluations = evaluators.filter(e => e.evaluationCompleted).length;
  const totalEvaluators = evaluators.length;
  const contextualizedCount = evaluators.filter(e => e.contextualized).length;
  const allEvaluationsCompleted = completedEvaluations === totalEvaluators;
  const allContextualized = contextualizedCount === totalEvaluators;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        onLogout={handleLogout}
        onProfileClick={() => navigate('/profile')}
        userData={userData}
      />
      
      <main className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold text-gray-900">Nominate And Manage Your Evaluators</h1>
              </div>
              
              <div className="space-y-2">
                <p className="text-gray-600">
                  {selectedPlan?.maxEvaluators > 5 
                    ? `Please nominate at least 5 evaluators for your assessment`
                    : 'Please nominate 5 evaluators for your assessment'}
                </p>
                <p className="text-gray-600">
                  You have nominated {evaluators.length} out of {selectedPlan?.maxEvaluators || 0} evaluators.
                </p>
                <p className="text-gray-600 font-bold mt-2">
                  As soon as you nominate an evaluator, they'll receive an email, and you'll be copied in.
                </p>
              </div>

              {!showForm && evaluators.length < (selectedPlan?.maxEvaluators || 0) && (
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full flex items-center justify-center space-x-2 bg-primary text-dark px-4 py-3 rounded-lg hover:bg-primary-hover transition-colors mt-4"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Evaluator</span>
                </button>
              )}
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-6 py-4 bg-red-50 border-b border-red-100"
                >
                  <p className="text-red-600">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="px-6 py-4">
              {showForm ? (
                <EvaluatorForm
                  onSubmit={handleAddEvaluator}
                  onCancel={() => setShowForm(false)}
                />
              ) : (
                <EvaluatorList
                  evaluators={evaluators}
                  onEdit={() => {}}
                  onDelete={handleDeleteEvaluator}
                  maxEvaluators={selectedPlan?.maxEvaluators || 0}
                  onUpdate={loadData}
                />
              )}
            </div>

            {selectedPlan && (
              <div className="px-6 pb-6">
                <FinalizeNominationsButton
                  maxEvaluators={selectedPlan.maxEvaluators}
                  currentEvaluators={evaluators.length}
                  allEvaluationsCompleted={allEvaluationsCompleted}
                  allContextualized={allContextualized}
                  onFinalize={handleFinalize}
                />
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}